/* Barra de ações pós-resposta — Copiar + Gerar documento (09/07/2026).
   CFM-safe INEGOCIÁVEL: todo documento gerado é RASCUNHO que o médico
   revisa e assina — o pedido enviado ao chat e o disclaimer do menu
   carregam esse enquadramento sempre. */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { DocRequestType } from '../types';

/** Ordem fixa dos tipos no menu "Gerar documento" */
const DOC_TYPES: readonly DocRequestType[] = [
  'atestado',
  'encaminhamento',
  'declaracao_comparecimento',
  'relatorio',
  'pericia',
] as const;

interface DocumentActionBarProps {
  /** Conteúdo da mensagem do assistant (alvo do botão Copiar) */
  content: string;
  /** true = desabilita ações (chat carregando / gate de email ativo) */
  disabled?: boolean;
  /** Callback ao escolher um tipo de documento no menu */
  onGenerateDocument: (docType: DocRequestType) => void;
  /** Função de tradução */
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Copia texto via clipboard com fallback execCommand (padrão do projeto) */
function copiarTexto(text: string): boolean {
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      return true;
    }
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/** Estilo base dos botões — discreto, não compete com o conteúdo */
const botaoBase: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--ink-soft)',
  color: 'var(--ink-mute)',
  fontFamily: 'var(--font-sans)',
  fontSize: '12px',
  lineHeight: 1.4,
  padding: '3px 10px',
  cursor: 'pointer',
  textShadow: 'none',
  boxShadow: 'none',
  transition: 'color 150ms ease-out, border-color 150ms ease-out',
};

/**
 * Barra de ações renderizada sob CADA mensagem do assistant (não-local).
 *
 * - [Copiar]: copia o conteúdo da mensagem (feedback "Copiado ✓" 2s).
 * - [Gerar documento ▾]: menu IN-FLOW (expande no fluxo, sem dropdown
 *   absoluto — decisão pra nunca quebrar layout mobile nem sofrer clipping
 *   no container com overflow). Escolher um tipo envia pelo chat o pedido
 *   natural "Gera o rascunho de X... pra eu revisar e assinar" + campo
 *   doc_request no body (telemetria backend).
 */
export function DocumentActionBar({
  content,
  disabled = false,
  onGenerateDocument,
  t,
}: DocumentActionBarProps) {
  const [copiado, setCopiado] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const copiadoTimerRef = useRef<number | null>(null);

  // Cleanup do timer de feedback ao desmontar
  useEffect(() => {
    return () => {
      if (copiadoTimerRef.current !== null) {
        window.clearTimeout(copiadoTimerRef.current);
      }
    };
  }, []);

  // Fecha o menu com clique fora ou Escape
  useEffect(() => {
    if (!menuAberto) return;
    function handleClickFora(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuAberto(false);
      }
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuAberto(false);
    }
    document.addEventListener('mousedown', handleClickFora);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickFora);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [menuAberto]);

  const handleCopiar = useCallback(() => {
    if (!copiarTexto(content)) return;
    setCopiado(true);
    if (copiadoTimerRef.current !== null) {
      window.clearTimeout(copiadoTimerRef.current);
    }
    copiadoTimerRef.current = window.setTimeout(() => setCopiado(false), 2000);
  }, [content]);

  const handleEscolherTipo = useCallback(
    (docType: DocRequestType) => {
      setMenuAberto(false);
      onGenerateDocument(docType);
    },
    [onGenerateDocument],
  );

  /** Hover sutil — só quando habilitado */
  const hoverOn = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    e.currentTarget.style.color = 'var(--ink-light)';
    e.currentTarget.style.borderColor = 'var(--ink-mute)';
  };
  const hoverOff = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.color = 'var(--ink-mute)';
    e.currentTarget.style.borderColor = 'var(--ink-soft)';
  };

  return (
    <div ref={containerRef} style={{ maxWidth: '80%' }}>
      {/* Linha de botões */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
        <button
          type="button"
          onClick={handleCopiar}
          disabled={disabled}
          aria-label={t('docbar.copy')}
          style={{
            ...botaoBase,
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: copiado ? 'var(--ink-light)' : 'var(--ink-mute)',
          }}
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
        >
          {copiado ? t('docbar.copied') : t('docbar.copy')}
        </button>

        <button
          type="button"
          onClick={() => setMenuAberto((prev) => !prev)}
          disabled={disabled}
          aria-expanded={menuAberto}
          aria-haspopup="menu"
          style={{
            ...botaoBase,
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: menuAberto ? 'var(--ink-light)' : 'var(--ink-mute)',
            borderColor: menuAberto ? 'var(--ink-mute)' : 'var(--ink-soft)',
          }}
          onMouseEnter={hoverOn}
          onMouseLeave={menuAberto ? undefined : hoverOff}
        >
          {t('docbar.generate')} {menuAberto ? '▴' : '▾'}
        </button>
      </div>

      {/* Menu in-flow — expande no fluxo (nunca quebra mobile) */}
      {menuAberto && (
        <div
          role="menu"
          aria-label={t('docbar.generate')}
          style={{
            marginTop: '6px',
            padding: '8px 10px',
            border: '1px solid var(--ink-soft)',
            background: 'var(--ink-sunken)',
          }}
        >
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {DOC_TYPES.map((docType) => (
              <button
                key={docType}
                type="button"
                role="menuitem"
                onClick={() => handleEscolherTipo(docType)}
                disabled={disabled}
                style={{
                  ...botaoBase,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: 'var(--ink-light)',
                }}
                onMouseEnter={(e) => {
                  if (disabled) return;
                  e.currentTarget.style.borderColor = 'var(--violet)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--ink-soft)';
                }}
              >
                {t(`docbar.types.${docType}`)}
              </button>
            ))}
          </div>
          {/* CFM-safe: sempre rascunho — o médico revisa e assina */}
          <div
            style={{
              marginTop: '6px',
              fontFamily: 'var(--font-sans)',
              fontSize: '11px',
              color: 'var(--ink-mute)',
              lineHeight: 1.5,
            }}
          >
            {t('docbar.disclaimer')}
          </div>
        </div>
      )}
    </div>
  );
}
